import CustomButton from 'components/CustomInput/Button';
import React, { useEffect, useState } from 'react';
import TextHeader from 'components/CustomText/TextHeader';
import { Checkbox } from '@material-ui/core';
import TextSubHeader from 'components/CustomText/TextSubHeader';
import CustomTextField from 'components/Input/CustomTextField';
import CustomDatePicker from 'components/Input/CustomDatePicker';
import CustomTable from 'components/CustomTable';
import useProgressReportData from './useProgressReportData';
import PopupInput from 'components/Application/TeamMemberForm/PopupInput';
import GeneralPostApprovalLayout from 'components/GeneralPostApprovalLayout';
import CustomRadio from 'components/CustomRadio';
import useSeriousAdverseEventsData from './useSeriousAdverseEventsData';

import useUploadDocData from './useUploadDocData';
import { postApprovalPermissionChecking, produce, waitUntilFormUpdate } from 'utility/general';
import useQuery from 'hooks/useQuery';
import { PROGRESS_REPORT } from 'utility/postApprovalLookUp';
import { useDispatch, useSelector } from 'react-redux';
import { GET_PROGRESS_REPORT, SAVE_PROGRESS_REPORT } from 'store/key';
import moment from 'moment';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { setGlobalLoading } from 'store/global/reducer';
import useDialog from 'components/ConfirmDialog/useDialog';
import {
    ERROR_MAX_LENGTH,
    GENERAL_RE_SUBMIT_SUCCESS,
    GENERAL_SAVE_SUCCESS,
    GENERAL_SUBMIT_SUCCESS,
    PLEASE_COMPLETE_THE_FORM,
} from 'utility/frontendMessage';
import styled from 'styled-components';

const SEvenRow = styled.div`
    display: flex;
    width: 100%;

    & > :nth-child(1) {
        flex: 1;
        display: flex;
        flex-shrink: 0;

        & > :nth-child(1) {
            flex-shrink: 0;
            width: ${(props) => props.labelWidth ?? 180}px;
        }

        & > :nth-child(2) {
            flex-grow: 1;
            padding-left: 5px;
            word-break: break-all;
        }
    }

    & > :nth-child(2) {
        flex: 1.5;

        display: flex;
        flex-shrink: 0;
        padding-left: 10px;

        & > :nth-child(1) {
            flex-shrink: 0;
            width: ${(props) => props.labelWidth ?? 210}px;
        }

        & > :nth-child(2) {
            flex-grow: 1;
            word-break: break-all;
        }
    }
`;

const ProgressReport = (props) => {
    const appId = useQuery('appId');
    const siteId = useQuery('siteId');
    const reportId = useQuery('reportId');
    const progressReport = useSelector((state) => state.progressReport);
    const status = progressReport?.remoteDetail?.lastStatus;
    const history = useHistory();
    const showDialog = useDialog();

    const isPermission = postApprovalPermissionChecking({ status, reportData: progressReport.remoteDetail });
    const signedFileId = progressReport.remoteDetail.signedFileId;
    const signedFileName = progressReport.remoteDetail.signedFileName;

    // enable from datepicker for progress report created before deployment of enchancement
    const cutOffDate = moment("2026-07-02");
    const createDate = moment.parseZone(progressReport.remoteDetail?.createDtm);
    const isDisableFromDatePicker = !reportId || createDate.isSameOrAfter(cutOffDate);

    const [signedUpload, setSignedUpload] = useState({ key: null, fileName: '' });
    const [validation, setValidation] = useState({});
    const [postData, setPostData] = useState({});
    const [forceErrorDisplay, setForceErrorDisplay] = useState(false);
    const [removeRequest, setRemoveRequest] = useState([]);
    const [maxLengthValidation, setMaxLengthValidation] = useState({});

    const dispatch = useDispatch();

    const {
        columns: uploadDocColumns,
        rows: uploadDocRows,
        defaultUploadDocFormList,
        onUploadDocEditSubmit,
        handleUploadDocUpdate,
        handleUploadDocDelete,
        showUploadDocDialog,
        setShowUploadDocDialog,
        addSupplements,
        deleteSupplements,
        resetUploadData,
    } = useUploadDocData({ isPermission, data: progressReport.remoteDetail });

    const {
        columns: adverseEventsColumns,
        rows: adverseEventsRows,
        defaultAdverseEventsFormList,
        selectedAdverseEventsRow,
        onAdverseEventsEditSubmit,
        handleAdverseEventsRecordUpdate,
        handleAdverseEventsDelete,
        handleAdverseEventsEditClick,
        showAdverseEventsDialog,
        setShowAdverseEventsDialog,
        setSelectedAdverseEventsRow,
        newSummaryOfLocalSeriousAdverseEvents,
        updateSummaryOfLocalSeriousAdverseEvents,
        deleteSummaryOfLocalSeriousAdverseEvents,
        resetTableData,
    } = useSeriousAdverseEventsData({ data: progressReport.remoteDetail });

    useEffect(() => {
        dispatch({
            type: GET_PROGRESS_REPORT,
            payload: {
                appId,
                siteId,
                docType: PROGRESS_REPORT,
                reportId,
                onPermissionError: () => {
                    history.push('/');
                },
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // update conditional validation
    useEffect(() => {
        if (!getValue('isNotYetStarted') && getValue('actualStudyStartDate')) {
            updateValidation('actualStudyStartDate', true);
        } else if (!getValue('isNotYetStarted') && !getValue('actualStudyStartDate')) {
            updateValidation('actualStudyStartDate', false);
        } else {
            // case of isNotYetStarted is checked, skip validation of actualStudyStartDate
            updateValidation('actualStudyStartDate', true);
        }

        // validation on part 8
        if (
            checkNullOrUndefined(getValue('continueAccordingToThePlan')) &&
            checkNullOrUndefined(getValue('extend')) &&
            checkNullOrUndefined(getValue('termination'))
        ) {
            updateValidation('checkEitherOne', false);
        } else {
            updateValidation('checkEitherOne', true);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [postData, progressReport.remoteDetail]);

    useEffect(() => {
        if (signedFileId && signedFileName) {
            setSignedUpload({ fileName: signedFileName, key: signedFileId });
        } else {
            setSignedUpload({ fileName: '', key: null });
        }
    }, [signedFileId, signedFileName]);

    const clearKey = (keys, timeout = 300) => {
        // wait for debounceChange interval
        removeKey(keys);
        setTimeout(() => {
            removeKey(keys);
        }, 300);
    };

    const removeKey = (keys) => {
        setPostData(
            produce((draft) => {
                keys.forEach((item) => {
                    delete draft[item];
                });
            }),
        );

        setRemoveRequest(
            produce((draft) => {
                keys.forEach((item) => {
                    if (!draft.includes(item)) {
                        draft.push(item);
                    }
                });
            }),
        );
    };

    const checkNullOrUndefined = (value) => {
        if (value === undefined || value === null) {
            return true;
        }

        return false;
    };

    const getValue = (key) => {
        if (!postData[key] && removeRequest.includes(key)) {
            return null;
        }

        return postData?.[key] ?? progressReport?.remoteDetail?.[key];
    };

    useEffect(() => {
        if (
            getValue('newEvidence') === false &&
            getValue('subjectsWillingnessToContinue') === false &&
            getValue('describeNewInformation')
        ) {
            clearKey(['describeNewInformation']);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getValue('newEvidence'), getValue('subjectsWillingnessToContinue')]);

    const updateValue = (key, value) => {
        setPostData(
            produce((draft) => {
                draft[key] = value;
            }),
        );
    };

    const updateValidation = (key, isValid) => {
        setValidation(
            produce((draft) => {
                draft[key] = isValid;
            }),
        );
    };

    const getRemoveRequest = () => {
        const updatedRemoveRequest = produce(removeRequest, (draft) => {
            Object.keys(postData).forEach((key) => {
                const index = draft.findIndex((item) => item === key);

                if (index !== -1) {
                    draft.splice(index, 1);
                }
            });
        });

        return updatedRemoveRequest;
    };

    const updateMaxLength = (key, value) => {
        setMaxLengthValidation(
            produce((draft) => {
                draft[key] = value;
            }),
        );
    };

    const detectChanges = () => {
        let hasChange = false;

        if (Object.values(postData).length > 0) {
            hasChange = true;
        }

        if (addSupplements.length > 0 || deleteSupplements.length > 0) {
            hasChange = true;
        }

        if (
            removeRequest.length > 0 ||
            newSummaryOfLocalSeriousAdverseEvents.length > 0 ||
            updateSummaryOfLocalSeriousAdverseEvents.length > 0 ||
            deleteSummaryOfLocalSeriousAdverseEvents.length > 0
        ) {
            hasChange = true;
        }

        if (signedUpload.isNewUpload) {
            hasChange = true;
        }

        return hasChange;
    };

    const { columns: changesColumns, rows: changesRows } = useProgressReportData({
        isPermission,
        onValueUpdate: updateValue,
        onValidationUpdate: updateValidation,
        postData,
        data: progressReport?.remoteDetail,
        clearKeys: clearKey,
        removeRequest,
        forceErrorDisplay,
    });

    const getSignedUpload = () => {
        if (signedUpload.isNewUpload) {
            return signedUpload;
        }

        return {
            key: progressReport?.remoteDetail?.signedFileId,
            fileName: progressReport?.remoteDetail?.signedFileName,
        };
    };

    const onFormSubmit = ({ isReSubmit }) => {
        onFormSave({ isSubmit: true, isReSubmit });
    };

    const onFormSave = ({ isSubmit = false, isResubmit, callback }) => {
        // let message = '';

        // if (!isSubmit) {
        //     message = GENERAL_SAVE_TIPS;
        // } else if (isResubmit) {
        //     message = GENERAL_RE_SUBMIT_TIPS;
        // } else if (!isResubmit) {
        //     message = GENERAL_SUBMIT_TIPS;
        // }

        // const isConfirm = showDialog({
        //     description: message,
        //     disableCancelButton: true,
        // });

        // if (!isConfirm) {
        //     return;
        // }

        const saveData = postData;
        saveData.addSupplements = addSupplements;
        saveData.deleteSupplements = deleteSupplements;
        saveData.newSummaryOfLocalSeriousAdverseEvents = newSummaryOfLocalSeriousAdverseEvents.map((item) => {
            delete item['id'];
            return item;
        });
        saveData.updateSummaryOfLocalSeriousAdverseEvents = updateSummaryOfLocalSeriousAdverseEvents;
        saveData.deleteSummaryOfLocalSeriousAdverseEvents = deleteSummaryOfLocalSeriousAdverseEvents;

        if (signedUpload.isNewUpload && signedUpload.fileName) {
            saveData.signedFileId = parseInt(signedUpload.key);
            saveData.signedFileName = signedUpload.fileName;
        }

        saveData.removeRequest = getRemoveRequest();

        // case of no draft version
        if (!reportId) {
            saveData.siteInfoId = parseInt(siteId);
            saveData.appId = parseInt(appId);
        } else {
            saveData.reportId = parseInt(reportId);
            saveData.appId = progressReport.remoteDetail.appId;
        }

        // handle not yet start logic, if value is null, set the value to false to server
        if (postData?.['isNotYetStarted'] === undefined && progressReport?.remoteDetail?.['isNotYetStarted'] === null) {
            saveData['isNotYetStarted'] = false;
        }

        setGlobalLoading(true);

        dispatch({
            type: SAVE_PROGRESS_REPORT,
            payload: {
                data: saveData,
                isSubmit,
                onError: () => {
                    setGlobalLoading(false);
                },
                callback: (resultReportId) => {
                    // case when save
                    if (!isSubmit) {
                        if (!reportId) {
                            history.replace(`/progress-report?reportId=${resultReportId}`);
                        }

                        setTimeout(() => {
                            dispatch({
                                type: GET_PROGRESS_REPORT,
                                payload: {
                                    appId,
                                    siteId,
                                    docType: PROGRESS_REPORT,
                                    reportId: resultReportId,
                                    callback: () => {
                                        callback?.(resultReportId);
                                    },
                                    isLoading: false,
                                    final: () => {
                                        setGlobalLoading(false);

                                        // if callback exist, the action will be print function, so disable the callback
                                        if (!callback) {
                                            showDialog({
                                                disableCancelButton: true,
                                                description: GENERAL_SAVE_SUCCESS,
                                            });
                                        }

                                        setPostData({});
                                        setRemoveRequest([]);
                                        resetTableData();
                                        resetUploadData();
                                        setSignedUpload({ key: 0, fileName: '' });
                                    },
                                },
                            });
                        }, 100);
                    } else if (isSubmit) {
                        setGlobalLoading(false);
                        showDialog({
                            disableCancelButton: true,
                            description: progressReport.remoteDetail.hasCCOAction
                                ? GENERAL_RE_SUBMIT_SUCCESS
                                : GENERAL_SUBMIT_SUCCESS,
                        });
                        history.push('/');
                    }
                },
            },
        });
    };

    useEffect(() => {
        if (!reportId) {
            updateValue('reportPeriodFrom', progressReport.remoteDetail.reportPeriodFrom);
        }
    }, [progressReport.remoteDetail.reportPeriodFrom]);

    useEffect(() => {
        let valid = adverseEventsRows.length > 0 ? true : false;
        if (getValue('mandatorySeriousAdverseEvents') === true) {
            updateValidation('mandatorySeriousAdverseEventsTable', valid);
        } else {
            updateValidation('mandatorySeriousAdverseEventsTable', true);
        }
    }, [getValue('mandatorySeriousAdverseEvents')]);

    return (
        <GeneralPostApprovalLayout
            type={PROGRESS_REPORT}
            isLoading={progressReport?.isLoading}
            isFormCreate={!reportId}
            onCreate={async ({ callback }) => {
                onFormSave({ isSubmit: false, isReSubmit: false, callback });
            }}
            onSubmit={async ({ isReSubmit }) => {
                onFormSubmit({ isReSubmit });
            }}
            checkSaveFormValid={async () => {
                // handling for debounceChange, if form is changing, wait for 300ms
                const hasChange = await waitUntilFormUpdate();

                if (hasChange) {
                    return;
                }

                const hasMaxLengthError = Object.values(maxLengthValidation).includes(false);

                if (hasMaxLengthError) {
                    return { valid: false, msg: ERROR_MAX_LENGTH };
                }

                return { valid: true };
            }}
            checkSubmitFormValid={async () => {
                const hasChange = await waitUntilFormUpdate();

                if (hasChange) {
                    return;
                }

                setForceErrorDisplay(true);

                if (
                    validation['mandatorySeriousAdverseEventsTable'] === false &&
                    getValue('mandatorySeriousAdverseEvents') === true
                ) {
                    return { valid: false, msg: 'Please add event data for Q5.' };
                }

                const hasError = Object.values(validation).includes(false);

                if (hasError) {
                    return { valid: false, msg: PLEASE_COMPLETE_THE_FORM };
                }

                return { valid: true };
            }}
            status={progressReport?.remoteDetail?.lastStatus}
            reportId={reportId}
            hasCCOAction={progressReport?.remoteDetail?.hasCCOAction}
            isPermission={isPermission}
            isViewOnly={!isPermission}
            signedUpload={getSignedUpload()}
            onSignedDocumentUploaded={(result) => {
                // result is in format {fileName: 'test.docx', key: 112, isNewUpload: true, file: FileObject}

                // file is removed
                if (!result.fileName) {
                    clearKey(['signedFileId', 'signedFileName']);
                    result.isNewUpload = true;
                    result.fileName = null;
                    result.key = null;
                }

                setSignedUpload(result);
            }}
            checkIsFormDataChanged={detectChanges}
            siteInfo={getValue('siteInstruction')}
        >
            {showUploadDocDialog && (
                <PopupInput
                    title={'Upload Supplementary Document'}
                    height="500px"
                    width="700px"
                    alignItems={'center'}
                    defaultFormList={defaultUploadDocFormList}
                    editRow={onUploadDocEditSubmit}
                    setOpenPopup={setShowUploadDocDialog}
                    tableList={uploadDocRows}
                    onSubmit={handleUploadDocUpdate}
                    isUpload
                />
            )}

            {showAdverseEventsDialog && (
                <PopupInput
                    title={'Summary of Serious Adverse Events'}
                    height="500px"
                    width="750px"
                    alignItems={'center'}
                    defaultFormList={defaultAdverseEventsFormList}
                    selectedRow={selectedAdverseEventsRow}
                    editRow={onAdverseEventsEditSubmit}
                    setOpenPopup={setShowAdverseEventsDialog}
                    tableList={adverseEventsRows}
                    onSubmit={handleAdverseEventsRecordUpdate}
                />
            )}
            <TextHeader style={{ textAlign: 'center', marginBottom: 10 }}>Research Progress Report Form</TextHeader>

            <TextSubHeader>1. Research Identification</TextSubHeader>

            <SEvenRow style={{ marginTop: 10 }}>
                <div>
                    <div>Scientific Title:</div>
                    <div>{getValue('scientificTitle')}</div>
                </div>
            </SEvenRow>

            <SEvenRow style={{ marginTop: 20 }}>
                <div>
                    <div>IRB/REC Ref. No.:</div>
                    <div>{getValue('refNo')}</div>
                </div>
                <div>
                    <div>Coordinating Investigator:</div>
                    <div>{getValue('ci')}</div>
                </div>
            </SEvenRow>

            <SEvenRow style={{ marginTop: 20 }}>
                <div>
                    <div>Protocol No.:</div>
                    <div>{getValue('protocolNo')}</div>
                </div>
                <div>
                    <div>Study Site of this progress report:</div>
                    <div>
                        <CustomTextField
                            disabled={!isPermission}
                            label="Study Site of this progress report"
                            value={getValue('studySite')}
                            forceErrorDisplay={forceErrorDisplay}
                            validators={['isRequired']}
                            maxLength={255}
                            debounceChange
                            isValidValue={(isValid, isMaxLengthValid) => {
                                updateValidation('studySite', isValid);
                                updateMaxLength('studySite', isMaxLengthValid);
                            }}
                            onChange={(e) => {
                                updateValue('studySite', e.target.value);
                            }}
                        />
                    </div>
                </div>
            </SEvenRow>

            <SEvenRow style={{ marginTop: 20 }}>
                <div>
                    <div>Anticipated Study End Date:</div>
                    <div>
                        {getValue('anticipatedStudyEndDate')
                            ? moment(getValue('anticipatedStudyEndDate')).format('YYYY-MM-DD')
                            : getValue('anticipatedStudyEndDate')}
                    </div>
                </div>
                <div>
                    <div>Site Principal Investigator of the above study site(s):</div>
                    <div>{getValue('sitePI')}</div>
                </div>
            </SEvenRow>
            <TextSubHeader style={{ paddingTop: 20 }}>2. Progress Report</TextSubHeader>

            <SEvenRow style={{ marginTop: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div>Report sequence no.:</div>
                    <div>{getValue('reportSeqNo') ?? '-'}</div>
                </div>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>Report Period (From) + (to):</div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ width: 150 }}>
                            <CustomDatePicker
                                disabled={!isPermission || isDisableFromDatePicker}
                                value={getValue('reportPeriodFrom')}
                                validators={['isRequired']}
                                forceErrorDisplay={forceErrorDisplay}
                                isValidValue={(isValid) => {
                                    updateValidation('reportPeriodFrom', isValid);
                                }}
                                onChange={(date) => {
                                    updateValue('reportPeriodFrom', date);
                                }}
                            />
                        </div>
                        <div style={{ padding: '0 10px', width: 15 }}>to</div>
                        <div style={{ width: 150 }}>
                            <CustomDatePicker
                                disabled={!isPermission}
                                value={getValue('reportPeriodTo')}
                                onChange={(date) => {
                                    updateValue('reportPeriodTo', date);
                                }}
                                isValidValue={(isValid) => {
                                    updateValidation('reportPeriodTo', isValid);
                                }}
                                validators={['isRequired']}
                                forceErrorDisplay={forceErrorDisplay}
                            />
                        </div>
                    </div>
                </div>
            </SEvenRow>

            <SEvenRow style={{ marginTop: 20 }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>Actual Study Start Date:</div>
                    <div style={{ display: 'flex' }}>
                        <div style={{ width: 200 }}>
                            <CustomDatePicker
                                disabled={!isPermission || getValue('isNotYetStarted')}
                                value={getValue('actualStudyStartDate')}
                                validators={!getValue('isNotYetStarted') ? ['isRequired'] : []}
                                forceErrorDisplay={forceErrorDisplay}
                                isValidValue={(isValid) => {
                                    updateValidation('actualStudyStartDate', isValid);
                                }}
                                minDate={new Date(progressReport?.remoteDetail?.decisionDate).getTime()}
                                maxDate={new Date().getTime()}
                                onChange={(date) => {
                                    updateValue('actualStudyStartDate', date);
                                }}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', marginLeft: 10 }}>
                            <Checkbox
                                disabled={!isPermission}
                                checked={getValue('isNotYetStarted')}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        clearKey(['actualStudyStartDate']);
                                    }
                                    updateValue('isNotYetStarted', e.target.checked);
                                }}
                                size="small"
                                color="primary"
                            />
                            Not yet started
                        </div>
                    </div>
                </div>
            </SEvenRow>

            <SEvenRow style={{ marginTop: 20 }}>
                <div>
                    <div>Planned Sample Size (site):</div>
                    <div>{getValue('plannedSampleSize')}</div>
                </div>

                <div>
                    <div>No. of Completed:</div>
                    <div>
                        <CustomTextField
                            disabled={!isPermission}
                            label="No. of Completed"
                            value={getValue('noOfCompleted')}
                            forceErrorDisplay={forceErrorDisplay}
                            validators={['isRequired']}
                            debounceChange
                            //numberField
                            style={{ width: '100%' }}
                            isValidValue={(isValid, isMaxLengthValid) => {
                                updateValidation('noOfCompleted', isValid);
                            }}
                            onChange={(e) => {
                                updateValue('noOfCompleted', e.target.value);
                            }}
                        />
                    </div>
                </div>
            </SEvenRow>

            <SEvenRow style={{ marginTop: 20 }}>
                <div>
                    <div>No. of Recruited^:</div>
                    <div>
                        <CustomTextField
                            //numberField
                            style={{ width: '100%' }}
                            disabled={!isPermission}
                            label="No. of Recruited"
                            value={getValue('noOfRecruited')}
                            forceErrorDisplay={forceErrorDisplay}
                            validators={['isRequired']}
                            debounceChange
                            isValidValue={(isValid, isMaxLengthValid) => {
                                updateValidation('noOfRecruited', isValid);
                            }}
                            onChange={(e) => {
                                updateValue('noOfRecruited', e.target.value);
                            }}
                        />
                    </div>
                </div>
            </SEvenRow>
            <SEvenRow style={{ marginTop: 20 }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>Any withdrawal?:</div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <CustomRadio
                            disabled={!isPermission}
                            value={getValue('anyWithdrawal')}
                            onChange={(e) => {
                                updateValue('anyWithdrawal', JSON.parse(e.target.value));

                                if (!JSON.parse(e.target.value)) {
                                    clearKey(['noOfWithdrawnAfterRecruited', 'withdrawalReasons']);
                                }
                            }}
                            isRequired
                            isValidValue={(isValid) => {
                                updateValidation('anyWithdrawal', isValid);
                            }}
                            forceErrorDisplay={forceErrorDisplay}
                            operators={[
                                { key: true, label: 'Yes' },
                                { key: false, label: 'No' },
                            ]}
                        />
                    </div>
                </div>

                <div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>No. of Withdrawn after recruited^:</div>
                    <div>
                        <CustomTextField
                            disabled={!isPermission || !getValue('anyWithdrawal')}
                            value={getValue('noOfWithdrawnAfterRecruited')}
                            forceErrorDisplay={forceErrorDisplay}
                            debounceChange
                            //numberField
                            style={{ width: '100%' }}
                            validators={!getValue('anyWithdrawal') ? [] : ['isRequired']}
                            isValidValue={(isValid, isMaxLengthValid) => {
                                updateValidation('noOfWithdrawnAfterRecruited', isValid);
                            }}
                            onChange={(e) => {
                                updateValue('noOfWithdrawnAfterRecruited', e.target.value);
                            }}
                        />
                    </div>
                </div>
            </SEvenRow>

            <SEvenRow style={{ marginTop: 20 }}>
                <div>
                    <div>Withdrawal reasons:</div>
                    <div>
                        <CustomTextField
                            disabled={!isPermission || !getValue('anyWithdrawal')}
                            label="Withdrawal reasons"
                            value={getValue('withdrawalReasons')}
                            forceErrorDisplay={forceErrorDisplay}
                            validators={!getValue('anyWithdrawal') ? [] : ['isRequired']}
                            maxLength={500}
                            isTextArea
                            debounceChange
                            textAreaMinRow={3}
                            isValidValue={(isValid, isMaxLengthValid) => {
                                updateValidation('withdrawalReasons', isValid);
                                updateMaxLength('withdrawalReasons', isMaxLengthValid);
                            }}
                            onChange={(e) => {
                                updateValue('withdrawalReasons', e.target.value);
                            }}
                        />
                    </div>
                </div>
            </SEvenRow>
            <div style={{ marginTop: 10 }}>
                <i>
                    ^Please submit Protocol Amendment Application if the no. of recruited will exceed the planned sample
                    size.
                </i>
            </div>
            <div style={{ marginTop: 20 }}>
                <TextSubHeader>3. Changes</TextSubHeader>
                <div style={{ marginTop: 10 }}>
                    <CustomTable textAlign="left" columns={changesColumns} rows={changesRows} isSortable={false} />
                </div>
            </div>
            <TextSubHeader style={{ marginTop: 20 }}>4. Updated Information</TextSubHeader>
            <div style={{ marginTop: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ marginRight: 20 }}>
                        Updated information that may affect a subject’s willingness to continue?
                    </div>
                    <CustomRadio
                        disabled={!isPermission}
                        forceErrorDisplay={forceErrorDisplay}
                        isRequired
                        isValidValue={(isValid) => {
                            updateValidation('subjectsWillingnessToContinue', isValid);
                        }}
                        onChange={(e) => {
                            updateValue('subjectsWillingnessToContinue', JSON.parse(e.target.value));
                        }}
                        value={getValue('subjectsWillingnessToContinue')}
                        operators={[
                            {
                                key: true,
                                label: 'Yes',
                            },
                            {
                                key: false,
                                label: 'No',
                            },
                        ]}
                    />
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ marginRight: 20 }}>New evidence that addresses the research hypothesis?</div>
                    <CustomRadio
                        disabled={!isPermission}
                        onChange={(e) => {
                            updateValue('newEvidence', JSON.parse(e.target.value));
                        }}
                        value={getValue('newEvidence')}
                        forceErrorDisplay={forceErrorDisplay}
                        isRequired
                        isValidValue={(isValid) => {
                            updateValidation('newEvidence', isValid);
                        }}
                        operators={[
                            {
                                key: true,
                                label: 'Yes',
                            },
                            {
                                key: false,
                                label: 'No',
                            },
                        ]}
                    />
                </div>
                <SEvenRow style={{ marginTop: 20 }}>
                    <div>
                        <div>Describe new information and actions taken:</div>
                        <div>
                            <CustomTextField
                                disabled={!isPermission}
                                allowAction={!!getValue('newEvidence') || !!getValue('subjectsWillingnessToContinue')}
                                label="Describe new information and actions taken"
                                value={getValue('describeNewInformation')}
                                forceErrorDisplay={forceErrorDisplay}
                                debounceChange
                                validators={
                                    getValue('newEvidence') === true ||
                                    getValue('subjectsWillingnessToContinue') === true
                                        ? ['isRequired']
                                        : []
                                }
                                maxLength={500}
                                isTextArea
                                textAreaMinRow={3}
                                isValidValue={(isValid, isMaxLengthValid) => {
                                    updateValidation('describeNewInformation', isValid);
                                    updateMaxLength('describeNewInformation', isMaxLengthValid);
                                }}
                                onChange={(e) => {
                                    updateValue('describeNewInformation', e.target.value);
                                }}
                            />
                        </div>
                    </div>
                </SEvenRow>
            </div>
            {/* <SeriousAdverseEvents isPermission={isPermission} classes={classes} /> */}

            <div style={{ display: 'flex', alignItems: 'center', marginTop: 20 }}>
                <TextSubHeader style={{ marginRight: 20 }}>5. Summary of Local Serious Adverse Events</TextSubHeader>
                <CustomRadio
                    disabled={!isPermission}
                    forceErrorDisplay={forceErrorDisplay}
                    isRequired
                    isValidValue={(isValid) => {
                        updateValidation('mandatorySeriousAdverseEvents', isValid);
                    }}
                    onChange={(e) => {
                        updateValue('mandatorySeriousAdverseEvents', JSON.parse(e.target.value));
                    }}
                    value={getValue('mandatorySeriousAdverseEvents')}
                    operators={[
                        {
                            key: true,
                            label: 'Yes',
                        },
                        {
                            key: false,
                            label: 'No/Not Applicable',
                        },
                    ]}
                />
            </div>
            {isPermission && (
                <CustomButton
                    disabled={!isPermission}
                    style={{ marginTop: 8 }}
                    onClick={() => {
                        setSelectedAdverseEventsRow(null);
                        setShowAdverseEventsDialog(true);
                    }}
                >
                    Add
                </CustomButton>
            )}
            <div style={{ marginTop: 10 }}>
                <CustomTable
                    disabled={!isPermission}
                    columns={adverseEventsColumns}
                    name={'mandatorySeriousAdverseEventsTable'}
                    rows={adverseEventsRows}
                    isEditAllow
                    isDeleteAllow
                    onAppFieldValidate={(name, value) => {
                        if (getValue('mandatorySeriousAdverseEvents') === true) {
                            updateValidation(name, value);
                        } else {
                            updateValidation(name, true);
                        }
                    }}
                    onDelete={handleAdverseEventsDelete}
                    onEdit={handleAdverseEventsEditClick}
                />
            </div>

            <div style={{ marginTop: 20, display: 'flex', alignItems: 'center' }}>
                Does the Local SAE affect the study?
                <div style={{ paddingLeft: 20, display: 'flex', alignItems: 'center' }}>
                    <CustomRadio
                        disabled={!isPermission}
                        isRequired={adverseEventsRows.length > 0}
                        onChange={(e) => {
                            updateValue('saeAffect', JSON.parse(e.target.value));

                            if (!JSON.parse(e.target.value)) {
                                clearKey(['saeDetails']);
                            }
                        }}
                        forceErrorDisplay={forceErrorDisplay}
                        isValidValue={(isValid) => {
                            updateValidation('saeAffect', isValid);
                        }}
                        value={getValue('saeAffect')}
                        operators={[
                            {
                                key: true,
                                label: 'Yes',
                            },
                            {
                                key: false,
                                label: 'No',
                            },
                        ]}
                    />
                </div>
            </div>
            <div>
                <CustomTextField
                    disabled={!isPermission || !getValue('saeAffect')}
                    label="Please provide details"
                    value={getValue('saeDetails')}
                    forceErrorDisplay={forceErrorDisplay}
                    validators={getValue('saeAffect') ? ['isRequired'] : []}
                    maxLength={500}
                    isTextArea
                    debounceChange
                    textAreaMinRow={3}
                    isValidValue={(isValid, isMaxLengthValid) => {
                        updateValidation('saeDetails', isValid);
                        updateMaxLength('saeDetails', isMaxLengthValid);
                    }}
                    onChange={(e) => {
                        updateValue('saeDetails', e.target.value);
                    }}
                />
            </div>
            <div style={{ marginTop: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <TextSubHeader>6. Complaints from Subjects</TextSubHeader>
                    <div style={{ paddingLeft: 20, display: 'flex', alignItems: 'center' }}>
                        <CustomRadio
                            disabled={!isPermission}
                            forceErrorDisplay={forceErrorDisplay}
                            isRequired
                            isValidValue={(isValid) => {
                                updateValidation('complaintsFromSubjects', isValid);
                            }}
                            onChange={(e) => {
                                updateValue('complaintsFromSubjects', JSON.parse(e.target.value));

                                if (!JSON.parse(e.target.value)) {
                                    clearKey(['complaintsDetails']);
                                }
                            }}
                            value={getValue('complaintsFromSubjects')}
                            operators={[
                                {
                                    key: true,
                                    label: 'Yes',
                                },
                                {
                                    key: false,
                                    label: 'No',
                                },
                            ]}
                        />
                    </div>
                </div>
                <div>
                    <CustomTextField
                        disabled={!isPermission || !getValue('complaintsFromSubjects')}
                        label="Summary of Complaints from Subjects"
                        value={getValue('complaintsDetails')}
                        forceErrorDisplay={forceErrorDisplay}
                        validators={getValue('complaintsFromSubjects') ? ['isRequired'] : []}
                        maxLength={500}
                        isTextArea
                        debounceChange
                        textAreaMinRow={6}
                        isValidValue={(isValid, isMaxLengthValid) => {
                            updateValidation('complaintsDetails', isValid);
                            updateMaxLength('complaintsDetails', isMaxLengthValid);
                        }}
                        onChange={(e) => {
                            updateValue('complaintsDetails', e.target.value);
                        }}
                    />
                </div>
            </div>
            <div style={{ marginTop: 20 }}>
                <TextSubHeader>7. Public Registration of Clinical Trial</TextSubHeader>
                {/* <PublicRegistration isPermission={isPermission} classes={classes} /> */}

                <div style={{ display: 'flex', alignItems: 'center', marginTop: 10 }}>
                    <div>
                        This clinical trial is registered in a publicly available, free to access, searchable clinical
                        trial registry?
                    </div>

                    <div style={{ paddingLeft: 20, display: 'flex', alignItems: 'center' }}>
                        <CustomRadio
                            disabled={!isPermission}
                            onChange={(e) => {
                                updateValue('registered', JSON.parse(e.target.value));

                                if (!JSON.parse(e.target.value)) {
                                    clearKey(['registerName', 'registryIdentifier']);
                                }
                            }}
                            value={getValue('registered')}
                            isRequired
                            isValidValue={(isValid) => {
                                updateValidation('registered', isValid);
                            }}
                            forceErrorDisplay={forceErrorDisplay}
                            operators={[
                                {
                                    key: true,
                                    label: 'Yes',
                                },
                                {
                                    key: false,
                                    label: 'No / Not Applicable',
                                },
                            ]}
                        />
                    </div>
                </div>

                <SEvenRow style={{ marginTop: 10 }}>
                    <div>
                        <div>Name of the clinical trial registry:</div>
                        <div>
                            <CustomTextField
                                disabled={!isPermission || !getValue('registered')}
                                isTextArea
                                debounceChange
                                label="Name of the clinical trial registry"
                                value={getValue('registerName')}
                                forceErrorDisplay={forceErrorDisplay}
                                validators={getValue('registered') ? ['isRequired'] : []}
                                maxLength={500}
                                isValidValue={(isValid, isMaxLengthValid) => {
                                    updateValidation('registerName', isValid);
                                    updateMaxLength('registerName', isMaxLengthValid);
                                }}
                                onChange={(e) => {
                                    updateValue('registerName', e.target.value);
                                }}
                            />
                        </div>
                    </div>
                </SEvenRow>

                <SEvenRow style={{ marginTop: 20 }}>
                    <div>
                        <div>Registry identifier code / number for this clinical trial:</div>
                        <div>
                            <CustomTextField
                                disabled={!isPermission || !getValue('registered')}
                                isTextArea
                                debounceChange
                                label="Registry identifier code / number for this clinical trial:"
                                value={getValue('registryIdentifier')}
                                forceErrorDisplay={forceErrorDisplay}
                                validators={getValue('registered') ? ['isRequired'] : []}
                                maxLength={500}
                                isValidValue={(isValid, isMaxLengthValid) => {
                                    updateValidation('registryIdentifier', isValid);
                                    updateMaxLength('registryIdentifier', isMaxLengthValid);
                                }}
                                onChange={(e) => {
                                    updateValue('registryIdentifier', e.target.value);
                                }}
                            />
                        </div>
                    </div>
                </SEvenRow>
            </div>
            <div style={{ marginTop: 20 }}>
                <TextSubHeader style={{ display: 'flex' }}>
                    8. Current Progress of Study
                    {!validation.checkEitherOne && forceErrorDisplay && (
                        <div style={{ color: 'red', marginLeft: 10 }}>(Either one should be checked below)</div>
                    )}
                </TextSubHeader>

                <div style={{ marginTop: 10 }}>
                    <CustomRadio
                        disabled={!isPermission}
                        onChange={(e) => {
                            // updateValue('continueAccordingToThePlan', JSON.parse(e.target.value));

                            setPostData(
                                produce((draft) => {
                                    draft.continueAccordingToThePlan = JSON.parse(e.target.value);
                                }),
                            );

                            clearKey(['extend', 'extendReason', 'termination', 'terminationReason']);
                        }}
                        value={getValue('continueAccordingToThePlan')}
                        operators={[
                            {
                                key: true,
                                label: 'Continue according to the plan',
                            },
                        ]}
                    />
                </div>
                <div>
                    <CustomRadio
                        disabled={!isPermission}
                        onChange={(e) => {
                            // updateValue('extend', JSON.parse(e.target.value));

                            setPostData(
                                produce((draft) => {
                                    draft.extend = JSON.parse(e.target.value);
                                }),
                            );

                            clearKey(['continueAccordingToThePlan', 'termination', 'terminationReason']);
                        }}
                        value={getValue('extend')}
                        operators={[
                            {
                                key: true,
                                label: 'Extend study period (please state the proposed extension date and submit Protocol Amendment Application)',
                            },
                        ]}
                    />
                    <div style={{ marginTop: 5, paddingLeft: 25 }}>
                        <CustomTextField
                            label="Reason for extension "
                            value={getValue('extendReason')}
                            forceErrorDisplay={forceErrorDisplay}
                            validators={getValue('extend') ? ['isRequired'] : []}
                            maxLength={500}
                            isTextArea
                            debounceChange
                            textAreaMinRow={3}
                            isValidValue={(isValid, isMaxLengthValid) => {
                                updateValidation('extendReason', isValid);
                                updateMaxLength('extendReason', isMaxLengthValid);
                            }}
                            onChange={(e) => {
                                updateValue('extendReason', e.target.value);
                            }}
                            disabled={!isPermission || !getValue('extend')}
                        />
                    </div>
                </div>
                <div>
                    <CustomRadio
                        disabled={!isPermission}
                        onChange={(e) => {
                            // updateValue('termination', JSON.parse(e.target.value));

                            setPostData(
                                produce((draft) => {
                                    draft.termination = JSON.parse(e.target.value);
                                }),
                            );

                            clearKey(['continueAccordingToThePlan', 'extend', 'extendReason']);
                        }}
                        value={getValue('termination')}
                        operators={[
                            {
                                key: true,
                                label: 'Premature termination',
                            },
                        ]}
                    />
                    <div style={{ marginTop: 5, paddingLeft: 25 }}>
                        <CustomTextField
                            label="Reason for Premature termination"
                            value={getValue('terminationReason')}
                            forceErrorDisplay={forceErrorDisplay}
                            validators={getValue('termination') ? ['isRequired'] : []}
                            debounceChange
                            maxLength={500}
                            isTextArea
                            textAreaMinRow={3}
                            isValidValue={(isValid, isMaxLengthValid) => {
                                updateValidation('terminationReason', isValid);
                                updateMaxLength('terminationReason', isMaxLengthValid);
                            }}
                            onChange={(e) => {
                                updateValue('terminationReason', e.target.value);
                            }}
                            disabled={!isPermission || !getValue('termination')}
                        />
                    </div>
                </div>
            </div>
            <div style={{ marginTop: 20 }}>
                <TextSubHeader>Upload Supplementary Document</TextSubHeader>
                {isPermission && (
                    <CustomButton
                        disabled={!isPermission}
                        style={{ marginTop: 8 }}
                        onClick={() => setShowUploadDocDialog(true)}
                    >
                        Add
                    </CustomButton>
                )}
                <div style={{ marginTop: 10 }}>
                    <CustomTable
                        disabled={!isPermission}
                        columns={uploadDocColumns}
                        rows={uploadDocRows}
                        isDeleteAllow
                        onDelete={handleUploadDocDelete}
                    />
                </div>
            </div>
        </GeneralPostApprovalLayout>
    );
};

export default ProgressReport;
